<script setup lang="ts">
    import { watch, ref, computed } from 'vue'
    import { getFileIcon, type Media, MediaType } from 'models'
    import { useMediaStore, useImageFallback } from 'composables'
    import PkLazyImg from './PkLazyImg.vue'

    const props = defineProps<{
        media: Media
        alt?: string
        width?: string | number
        height?: string | number
    }>()

    const { getDownloadHandle } = useMediaStore()

    const src = ref<string>()
    const isLoading = ref(false)
    watch(
        () => props.media,
        async (newValue) => {
            src.value = undefined
            if (newValue) {
                isLoading.value = true
                try {
                    src.value = (await getDownloadHandle(newValue.src))
                        ?.downloadHandle.previewSasuri
                } catch {
                    src.value = undefined
                } finally {
                    isLoading.value = false
                }
            }
        },
        { immediate: true },
    )

    const iconName = computed(() => {
        switch (props.media.src.type) {
            case MediaType.picture:
                return 'ri:file-image-line'
            case MediaType.video:
                return 'ri:file-video-line'
            case MediaType.audio:
                return 'ri:file-music-line'
            default:
                return getFileIcon(props.media.src.extension)
        }
    })

    // A dead preview url (expired sas, thumbnail not generated yet) falls back
    // to the file-type icon. Keyed by url, so the next sas refresh retries.
    const { canShowImage, onImageError } = useImageFallback()
    const imageSrc = computed(() =>
        canShowImage(src.value) ? src.value : undefined,
    )
</script>

<template>
    <picture class="relative" :class="{ 'vv-skeleton': isLoading }">
        <div
            v-if="!imageSrc"
            class="aspect-square flex items-center justify-center text-18"
            :style="{
                width: `${width}px`,
                height: `${height}px`,
            }">
            <VvIcon :name="iconName" />
        </div>
        <PkLazyImg
            v-else
            :src="imageSrc"
            :alt
            :width
            :height
            class="block w-full rounded-md"
            @error="onImageError(imageSrc)" />
        <div
            v-if="isLoading"
            class="absolute inset-0 vv-skeleton__item z-1 h-auto"></div>
    </picture>
</template>
